import { useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Newspaper, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button, buttonVariants } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'

type NewsSidebarProps = {
  items: wowaudit.GlobalNewsItem[]
  canManage: boolean
}

const NewsSidebar = ({ items, canManage }: NewsSidebarProps) => {
  return (
    <>
      <nav className='hidden lg:flex flex-col gap-3 shrink-0 w-64 border-r border-border pr-8'>
        <NewsSidebarList items={items} canManage={canManage} />
      </nav>

      <NewsSidebarSheet items={items} canManage={canManage} />
    </>
  )
}

const NewsSidebarSheet = ({ items, canManage }: NewsSidebarProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()

  useEffect(() => {
    setOpen(false)
  }, [pathname])

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant='outline' size='sm' className='w-full lg:hidden'>
          <Newspaper className='w-4 h-4 mr-2' />
          {t('news.all-posts')}
        </Button>
      </SheetTrigger>
      <SheetContent
        side='left'
        aria-describedby={undefined}
        className='flex w-[85vw] flex-col gap-3 overflow-y-auto p-4 pt-12'
      >
        <SheetTitle>{t('shared.news')}</SheetTitle>
        <NewsSidebarList items={items} canManage={canManage} />
      </SheetContent>
    </Sheet>
  )
}

const NewsSidebarList = ({ items, canManage }: NewsSidebarProps) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const defaultId = String(
    items.find((item) => item.published)?.id ?? items[0]?.id,
  )

  return (
    <>
      {canManage && (
        <Link
          to='/news/new'
          className={buttonVariants({ variant: 'default', size: 'sm' })}
        >
          <Plus className='w-4 h-4 mr-2' />
          {t('news.new-post')}
        </Link>
      )}

      <div className='flex flex-col gap-0.5'>
        {items.map((item) => (
          <Link
            key={item.id}
            to={`/news/${item.id}`}
            className={cn(
              'block rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent',
              String(item.id) === (id ?? defaultId) &&
                'bg-accent font-medium text-primary',
            )}
          >
            <div className='flex items-center justify-between gap-2'>
              <span
                className={cn(
                  'truncate',
                  !item.title && 'italic text-muted-foreground',
                )}
              >
                {item.title || t('news.no-title')}
              </span>
              {!item.published && (
                <Badge variant='outline' className='shrink-0'>
                  {t('news.draft')}
                </Badge>
              )}
            </div>
            {item.firstPublishedAt && (
              <div className='text-xs text-muted-foreground'>
                {dayjs(item.firstPublishedAt).format('MMM D, YYYY')}
              </div>
            )}
          </Link>
        ))}
      </div>
    </>
  )
}

export default NewsSidebar
